import React, {useState, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {User, Organization, Role, PlanType} from '../types.ts';
import {InviteResponse, OrganizationMemberWithStatus, DowngradePreviewResponse} from '../services/apiService.ts';
import {
    User as UserIcon,
    Building2,
    Plus,
    Mail,
    CheckCircle2,
    Clock,
    Trash2,
    Crown,
    Shield,
    UserCircle2,
    Briefcase,
    MailOpen,
    X,
    AlertCircle,
    CreditCard,
    ChevronRight,
    Minus
} from 'lucide-react';
import Avatar from "boring-avatars";
import {Button} from './ui/Button';
import {Card} from './ui/Card';
import {Field} from './ui/Input';
import {Tag} from './ui/Tag';

interface SettingsProps {
    user: User;
    organizations: Organization[];
    currentOrganizationId: string;
    orgMembers?: OrganizationMemberWithStatus[];
    pendingInvites?: InviteResponse[];
    onInviteMember: (email: string, role: Role) => Promise<void>;
    onAcceptInvite?: (inviteId: string) => Promise<void>;
    onRejectInvite?: (inviteId: string) => Promise<void>;
    onCancelInvite?: (inviteId: string) => Promise<void>;
    onCreateOrganization: (name: string) => void;
    onSwitchOrganization: (orgId: string) => void;
    onChangePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
    onUpgradePlan?: () => Promise<void>;
    onDowngradePlan?: (reason?: string) => Promise<void>;
    onGetDowngradePreview?: () => Promise<DowngradePreviewResponse>;
    onNavigateToBilling?: () => void;
}

type Tab = 'profile' | 'organization';

export const Settings: React.FC<SettingsProps> = ({
                                                      user,
                                                      organizations,
                                                      currentOrganizationId,
                                                      orgMembers = [],
                                                      pendingInvites = [],
                                                      onInviteMember,
                                                      onAcceptInvite,
                                                      onRejectInvite,
                                                      onCancelInvite,
                                                      onCreateOrganization,
                                                      onSwitchOrganization,
                                                      onChangePassword,
                                                      onUpgradePlan,
                                                      onDowngradePlan,
                                                      onGetDowngradePreview,
                                                      onNavigateToBilling
                                                  }) => {
    const location = useLocation();
    const navigate = useNavigate();
    // The active tab lives in the URL hash (#profile / #organization) so it can be
    // linked to directly (e.g. /settings#organization) and survives a refresh.
    const activeTab: Tab = location.hash === '#organization' ? 'organization' : 'profile';
    const setActiveTab = (tab: Tab) => navigate({pathname: location.pathname, hash: tab});
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<Role>('member');
    const [newOrgName, setNewOrgName] = useState('');
    const [isCreatingOrg, setIsCreatingOrg] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [acceptingInviteId, setAcceptingInviteId] = useState<string | null>(null);
    const [rejectingInviteId, setRejectingInviteId] = useState<string | null>(null);
    const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

    // Billing state
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [downgradeLoading, setDowngradeLoading] = useState(false);
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [downgradeReason, setDowngradeReason] = useState('');
    const [downgradePreview, setDowngradePreview] = useState<DowngradePreviewResponse | null>(null);
    const [billingError, setBillingError] = useState<string | null>(null);
    const [billingSuccess, setBillingSuccess] = useState<string | null>(null);

    const currentOrg = organizations.find(o => o.id === currentOrganizationId);

    // Separate active members from pending invites (sent by this org)
    const activeMembers = useMemo(() =>
        orgMembers.filter(m => m.status === 'active'),
        [orgMembers]
    );
    const sentInvites = useMemo(() =>
        orgMembers.filter(m => m.status === 'pending'),
        [orgMembers]
    );

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (inviteEmail && !inviteLoading) {
            setInviteLoading(true);
            setInviteError(null);
            setInviteSuccess(null);
            try {
                await onInviteMember(inviteEmail, inviteRole);
                setInviteSuccess(`Invitation sent to ${inviteEmail}`);
                setInviteEmail('');
                setInviteRole('member');
                // Clear success message after 5 seconds
                setTimeout(() => setInviteSuccess(null), 5000);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to send invite';
                setInviteError(message);
            } finally {
                setInviteLoading(false);
            }
        }
    };

    const handleCancelSentInvite = async (inviteId: string) => {
        if (!onCancelInvite) return;
        setCancellingInviteId(inviteId);
        try {
            await onCancelInvite(inviteId);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to cancel invite';
            setInviteError(message);
        } finally {
            setCancellingInviteId(null);
        }
    };

    const handleAcceptInvite = async (inviteId: string) => {
        if (!onAcceptInvite) return;
        setAcceptingInviteId(inviteId);
        try {
            await onAcceptInvite(inviteId);
        } finally {
            setAcceptingInviteId(null);
        }
    };

    const handleRejectInvite = async (inviteId: string) => {
        if (!onRejectInvite) return;
        setRejectingInviteId(inviteId);
        try {
            await onRejectInvite(inviteId);
        } finally {
            setRejectingInviteId(null);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!onChangePassword) return;

        setPasswordError(null);
        setPasswordSuccess(null);

        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError('New password must be at least 8 characters');
            return;
        }

        setPasswordLoading(true);
        try {
            await onChangePassword(currentPassword, newPassword);
            setPasswordSuccess('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordSuccess(null), 5000);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to change password';
            setPasswordError(message);
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCreateOrg = (e: React.FormEvent) => {
        e.preventDefault();
        if (newOrgName) {
            onCreateOrganization(newOrgName);
            setNewOrgName('');
            setIsCreatingOrg(false);
        }
    };

    const handleUpgrade = async () => {
        if (!onUpgradePlan) return;
        setUpgradeLoading(true);
        setBillingError(null);
        try {
            await onUpgradePlan();
            setBillingSuccess('Successfully upgraded to Pro!');
            setTimeout(() => setBillingSuccess(null), 5000);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to upgrade plan';
            setBillingError(message);
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleOpenDowngradeModal = async () => {
        if (!onGetDowngradePreview) return;
        try {
            const preview = await onGetDowngradePreview();
            setDowngradePreview(preview);
            setShowDowngradeModal(true);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load downgrade preview';
            setBillingError(message);
        }
    };

    const handleConfirmDowngrade = async () => {
        if (!onDowngradePlan) return;
        setDowngradeLoading(true);
        setBillingError(null);
        try {
            await onDowngradePlan(downgradeReason || undefined);
            setBillingSuccess('Your subscription will cancel at the end of the billing period. You keep Pro until then, then move to Free.');
            setShowDowngradeModal(false);
            setDowngradeReason('');
            setDowngradePreview(null);
            setTimeout(() => setBillingSuccess(null), 5000);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to downgrade plan';
            setBillingError(message);
        } finally {
            setDowngradeLoading(false);
        }
    };

    const getRoleBadge = (role: Role) => {
        switch (role) {
            case 'owner':
                return <Tag tone="amber" className="gap-1"><Crown
                    size={12}/> Owner</Tag>;
            case 'maintainer':
                return <Tag tone="sky" className="gap-1"><Shield
                    size={12}/> Maintainer</Tag>;
            case 'member':
                return <Tag tone="slate" className="gap-1"><UserCircle2
                    size={12}/> Member</Tag>;
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">

            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 md:gap-4 border-b border-[#e4e0d2] pb-1">
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`flex items-center gap-2 px-4 py-3 text-sm md:text-base border-b-2 transition-all ${activeTab === 'profile' ? 'border-[#22c55e] text-[#15803d] font-semibold' : 'border-transparent text-[#4f4f48] hover:text-[#0a0a0a] font-semibold'}`}
                >
                    <UserIcon size={18}/> My Profile
                </button>
                <button
                    onClick={() => setActiveTab('organization')}
                    className={`flex items-center gap-2 px-4 py-3 text-sm md:text-base border-b-2 transition-all ${activeTab === 'organization' ? 'border-[#22c55e] text-[#15803d] font-semibold' : 'border-transparent text-[#4f4f48] hover:text-[#0a0a0a] font-semibold'}`}
                >
                    <Building2 size={18}/> Organization Settings
                </button>
            </div>

            {/* Content Area */}
            <Card className="p-6 md:p-8">

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="space-y-8">
                        <div className="max-w-lg space-y-6">
                            <h2 className="text-xl font-bold text-[#0a0a0a]">Personal
                                Information</h2>
                            <div className="flex items-center gap-6">
                                <Avatar name={user.email} variant={"beam"}/>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label
                                        className="block text-sm font-semibold text-[#4f4f48] mb-1.5">Full
                                        Name</label>
                                    <input type="text" value={user.full_name} readOnly
                                           className="w-full rounded-xl border border-[#d3cdba] bg-[#faf9f3] px-3.5 py-2.5 font-medium text-[#4f4f48] cursor-not-allowed"/>
                                </div>
                                <div>
                                    <label
                                        className="block text-sm font-semibold text-[#4f4f48] mb-1.5">Email
                                        Address</label>
                                    <input type="email" value={user.email} readOnly
                                           className="w-full rounded-xl border border-[#d3cdba] bg-[#faf9f3] px-3.5 py-2.5 font-medium text-[#4f4f48] cursor-not-allowed"/>
                                </div>
                            </div>
                        </div>

                        {/* Change Password Section */}
                        {onChangePassword && (
                            <div className="max-w-lg space-y-6 pt-6 border-t border-[#e4e0d2]">
                                <h2 className="text-xl font-bold text-[#0a0a0a]">Change Password</h2>
                                {passwordError && (
                                    <div className="p-3 bg-[#ffe4e6] text-[#be123c] border border-[#fecdd3] rounded-lg flex items-center gap-2">
                                        <AlertCircle size={18}/>
                                        <span className="font-medium">{passwordError}</span>
                                        <button onClick={() => setPasswordError(null)} className="ml-auto p-1 hover:bg-[#fecdd3] rounded">
                                            <X size={16}/>
                                        </button>
                                    </div>
                                )}
                                {passwordSuccess && (
                                    <div className="p-3 bg-[#dcfce7] text-[#15803d] border border-[#bbf7d0] rounded-lg flex items-center gap-2">
                                        <CheckCircle2 size={18}/>
                                        <span className="font-medium">{passwordSuccess}</span>
                                        <button onClick={() => setPasswordSuccess(null)} className="ml-auto p-1 hover:bg-[#bbf7d0] rounded">
                                            <X size={16}/>
                                        </button>
                                    </div>
                                )}
                                <form onSubmit={handleChangePassword} className="space-y-4">
                                    <Field
                                        label="Current Password"
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        required
                                        disabled={passwordLoading}
                                    />
                                    <div>
                                        <Field
                                            label="New Password"
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            disabled={passwordLoading}
                                            minLength={8}
                                        />
                                        <p className="mt-1 text-xs text-[#4f4f48]">Must be at least 8 characters</p>
                                    </div>
                                    <Field
                                        label="Confirm New Password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        disabled={passwordLoading}
                                        minLength={8}
                                    />
                                    <Button
                                        type="submit"
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? 'Changing...' : 'Change Password'}
                                    </Button>
                                </form>
                            </div>
                        )}

                        {/* Pending Invitations (Received) */}
                        {pendingInvites.length > 0 && (
                            <div className="bg-[#fef3c7] p-6 rounded-xl border border-[#e4e0d2]">
                                <h3 className="text-lg font-bold text-[#92600a] mb-4 flex items-center gap-2">
                                    <MailOpen size={20}/> Pending Invitations ({pendingInvites.length})
                                </h3>
                                <p className="text-sm text-[#92600a] mb-4">You've been invited to join the following organizations:</p>
                                <div className="space-y-3">
                                    {pendingInvites.map((invite) => (
                                        <div key={invite.id} className="bg-white p-4 rounded-lg border border-[#e4e0d2] flex flex-col md:flex-row md:items-center justify-between gap-3">
                                            <div>
                                                <div className="font-bold text-[#0a0a0a]">{invite.organization_name}</div>
                                                <div className="text-sm text-[#4f4f48]">
                                                    Invited by {invite.invited_by_name} as <span className="font-medium capitalize">{invite.role}</span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleAcceptInvite(invite.id)}
                                                    disabled={acceptingInviteId === invite.id || rejectingInviteId === invite.id}
                                                >
                                                    {acceptingInviteId === invite.id ? 'Accepting...' : 'Accept'}
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => handleRejectInvite(invite.id)}
                                                    disabled={acceptingInviteId === invite.id || rejectingInviteId === invite.id}
                                                >
                                                    {rejectingInviteId === invite.id ? 'Declining...' : 'Decline'}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Organization Tab */}
                {activeTab === 'organization' && (
                    <div className="space-y-10">

                        {/* Workspace Switcher / Creator Header */}
                        <div className="bg-[#faf9f3] p-4 rounded-xl border border-[#e4e0d2]">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-[#4f4f48] uppercase tracking-wide flex items-center gap-2">
                                    <Briefcase size={14}/> Switch Workspace
                                </h3>
                                {!isCreatingOrg && (
                                    <button
                                        onClick={() => setIsCreatingOrg(true)}
                                        className="text-xs font-semibold text-[#15803d] hover:text-[#0a0a0a] hover:underline flex items-center gap-1"
                                    >
                                        <Plus size={14} strokeWidth={3}/> Create New
                                    </button>
                                )}
                            </div>

                            {isCreatingOrg ? (
                                <div className="animate-in slide-in-from-top-2">
                                    <form onSubmit={handleCreateOrg}
                                          className="flex gap-2 items-center">
                                        <input
                                            type="text"
                                            placeholder="New Workspace Name"
                                            value={newOrgName}
                                            onChange={(e) => setNewOrgName(e.target.value)}
                                            className="flex-1 rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 text-sm font-medium text-[#0a0a0a] transition-all placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30"
                                            autoFocus
                                        />
                                        <Button type="submit" variant="primary">Create</Button>
                                        <button type="button"
                                                onClick={() => setIsCreatingOrg(false)}
                                                className="text-[#4f4f48] font-semibold px-2 text-sm hover:text-[#0a0a0a]">Cancel
                                        </button>
                                    </form>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {organizations.map(org => (
                                        <button
                                            key={org.id}
                                            onClick={() => onSwitchOrganization(org.id)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                                                org.id === currentOrganizationId
                                                    ? 'bg-[#dcfce7] border-[#bbf7d0] text-[#15803d]'
                                                    : 'bg-white border-[#e4e0d2] text-[#4f4f48] hover:border-[#d3cdba] hover:bg-[#faf9f3]'
                                            }`}
                                        >
                                            {org.name}
                                            {org.id === currentOrganizationId && <span
                                                className="ml-2 inline-flex items-center rounded-full border border-[#bbf7d0] bg-[#dcfce7] px-2 py-0.5 text-[10px] font-semibold text-[#15803d]">ACTIVE</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {currentOrg && (
                            <>
                                {/* Header */}
                                <div
                                    className="flex items-center justify-between border-b border-[#e4e0d2] pb-4">
                                    <div>
                                        <h2 className="text-2xl font-bold text-[#0a0a0a]">{currentOrg.name}</h2>
                                        <p className="text-[#4f4f48] font-semibold text-sm">Organization
                                            Settings</p>
                                    </div>
                                    <div className="text-right">
                                        <span
                                            className="text-xs font-semibold text-[#4f4f48] uppercase">Current Role</span>
                                        <div className="mt-1">{getRoleBadge(currentOrg.role)}</div>
                                    </div>
                                </div>

                                {/* Invite Section */}
                                <div
                                    className="bg-[#f3e8ff] p-6 rounded-xl border border-[#e4e0d2]">
                                    <h3 className="text-lg font-bold text-[#7e22ce] mb-4 flex items-center gap-2">
                                        <Mail size={20}/> Invite New Member
                                    </h3>
                                    {inviteError && (
                                        <div className="mb-4 p-3 bg-[#ffe4e6] text-[#be123c] border border-[#fecdd3] rounded-lg flex items-center gap-2">
                                            <AlertCircle size={18}/>
                                            <span className="font-medium">{inviteError}</span>
                                            <button onClick={() => setInviteError(null)} className="ml-auto p-1 hover:bg-[#fecdd3] rounded">
                                                <X size={16}/>
                                            </button>
                                        </div>
                                    )}
                                    {inviteSuccess && (
                                        <div className="mb-4 p-3 bg-[#dcfce7] text-[#15803d] border border-[#bbf7d0] rounded-lg flex items-center gap-2">
                                            <CheckCircle2 size={18}/>
                                            <span className="font-medium">{inviteSuccess}</span>
                                            <button onClick={() => setInviteSuccess(null)} className="ml-auto p-1 hover:bg-[#bbf7d0] rounded">
                                                <X size={16}/>
                                            </button>
                                        </div>
                                    )}
                                    <form onSubmit={handleInvite}
                                          className="flex flex-col md:flex-row gap-3">
                                        <input
                                            type="email"
                                            placeholder="colleague@example.com"
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            className="flex-1 rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-medium text-[#0a0a0a] transition-all placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 disabled:opacity-50"
                                            required
                                            disabled={inviteLoading}
                                        />
                                        <select
                                            value={inviteRole}
                                            onChange={(e) => setInviteRole(e.target.value as Role)}
                                            className="rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 font-semibold text-[#0a0a0a] transition-all focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 disabled:opacity-50"
                                            disabled={inviteLoading}
                                        >
                                            <option value="member">Member</option>
                                            <option value="maintainer">Maintainer</option>
                                            <option value="owner">Owner</option>
                                        </select>
                                        <Button type="submit"
                                                disabled={inviteLoading}
                                                className="whitespace-nowrap">
                                            {inviteLoading ? 'Sending...' : 'Send Invite'}
                                        </Button>
                                    </form>
                                </div>

                                {/* Pending Invites (Sent) Section */}
                                {sentInvites.length > 0 && (
                                    <div className="bg-[#faf9f3] p-6 rounded-xl border border-[#e4e0d2]">
                                        <h3 className="text-lg font-bold text-[#0a0a0a] mb-4 flex items-center gap-2">
                                            <Clock size={20}/> Pending Invites ({sentInvites.length})
                                        </h3>
                                        <div className="space-y-3">
                                            {sentInvites.map((invite) => (
                                                <div key={invite.id} className="bg-white p-4 rounded-lg border border-[#e4e0d2] flex flex-col md:flex-row md:items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                                                            <Avatar name={invite.email} variant="beam"/>
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-[#0a0a0a] text-sm">{invite.email}</div>
                                                            <div className="text-xs text-[#4f4f48]">
                                                                Invited as <span className="font-medium capitalize">{invite.role}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleCancelSentInvite(invite.id)}
                                                        disabled={cancellingInviteId === invite.id}
                                                        className="text-[#be123c] hover:text-[#9f1239] font-semibold text-sm px-3 py-1.5 hover:bg-[#ffe4e6] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                    >
                                                        <X size={16}/>
                                                        {cancellingInviteId === invite.id ? 'Cancelling...' : 'Cancel Invite'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Active Members List */}
                                <div>
                                    <h3 className="text-lg font-bold text-[#0a0a0a] mb-4">Team
                                        Members ({activeMembers.length})</h3>
                                    <div
                                        className="overflow-hidden border border-[#e4e0d2] rounded-xl">
                                        <table className="w-full text-left border-collapse">
                                            <thead
                                                className="bg-[#faf9f3] border-b border-[#e4e0d2]">
                                            <tr>
                                                <th className="px-6 py-4 text-xs font-bold text-[#4f4f48] uppercase tracking-wide">User</th>
                                                <th className="px-6 py-4 text-xs font-bold text-[#4f4f48] uppercase tracking-wide">Role</th>
                                                <th className="px-6 py-4 text-xs font-bold text-[#4f4f48] uppercase tracking-wide text-right">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#ece8db]">
                                            {activeMembers.map((member) => (
                                                <tr key={member.id}
                                                    className="hover:bg-[#faf9f3] transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                                                                <Avatar name={member.email}
                                                                        variant={"beam"}
                                                                />
                                                            </div>
                                                            <div>
                                                                <div
                                                                    className="font-bold text-[#0a0a0a] text-sm">{member.full_name || member.email}</div>
                                                                <div
                                                                    className="text-xs text-[#4f4f48]">{member.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {getRoleBadge(member.role as Role)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button
                                                            className="text-[#9a948a] hover:text-[#be123c] p-2 hover:bg-[#ffe4e6] rounded-lg transition-colors">
                                                            <Trash2 size={18}/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Billing & Plan Section */}
                                <div className="pt-6 border-t border-[#e4e0d2]">
                                    <h3 className="text-lg font-bold text-[#0a0a0a] mb-4 flex items-center gap-2">
                                        <CreditCard size={20}/> Billing & Plan
                                    </h3>

                                    {billingError && (
                                        <div className="mb-4 p-3 bg-[#ffe4e6] text-[#be123c] border border-[#fecdd3] rounded-lg flex items-center gap-2">
                                            <AlertCircle size={18}/>
                                            <span className="font-medium">{billingError}</span>
                                            <button onClick={() => setBillingError(null)} className="ml-auto p-1 hover:bg-[#fecdd3] rounded">
                                                <X size={16}/>
                                            </button>
                                        </div>
                                    )}
                                    {billingSuccess && (
                                        <div className="mb-4 p-3 bg-[#dcfce7] text-[#15803d] border border-[#bbf7d0] rounded-lg flex items-center gap-2">
                                            <CheckCircle2 size={18}/>
                                            <span className="font-medium">{billingSuccess}</span>
                                            <button onClick={() => setBillingSuccess(null)} className="ml-auto p-1 hover:bg-[#bbf7d0] rounded">
                                                <X size={16}/>
                                            </button>
                                        </div>
                                    )}

                                    {/* Current Plan Card */}
                                    <div className="bg-[#faf9f3] border border-[#e4e0d2] rounded-xl p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <span className="text-xs font-semibold text-[#4f4f48] uppercase tracking-wide">Current Plan</span>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {currentOrg.plan === 'pro' ? (
                                                        <span className="text-xl font-bold text-[#15803d]">Pro</span>
                                                    ) : (
                                                        <span className="text-xl font-bold text-[#0a0a0a]">Free</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`px-4 py-2 rounded-lg font-bold text-sm ${
                                                currentOrg.plan === 'pro'
                                                    ? 'bg-[#dcfce7] text-[#15803d]'
                                                    : 'bg-[#ece8db] text-[#6f6b60]'
                                            }`}>
                                                {currentOrg.plan === 'pro' ? 'Unlimited Rules' : '1 Active Rule'}
                                            </div>
                                        </div>

                                        <p className="text-[#4f4f48] text-sm mb-4">
                                            {currentOrg.plan === 'pro'
                                                ? 'You have unlimited active rules with the Pro plan.'
                                                : 'The Free tier allows 1 active rule at a time. Upgrade to Pro for unlimited rules.'}
                                        </p>

                                        {onNavigateToBilling ? (
                                            <div className="flex items-center gap-4">
                                                <Button
                                                    variant="primary"
                                                    onClick={onNavigateToBilling}
                                                >
                                                    View Plans & Billing
                                                    <ChevronRight size={18} />
                                                </Button>
                                                {currentOrg.plan === 'pro' && onGetDowngradePreview && (
                                                    <button
                                                        onClick={handleOpenDowngradeModal}
                                                        className="text-[#4f4f48] hover:text-[#0a0a0a] font-medium text-sm underline"
                                                    >
                                                        Downgrade to Free
                                                    </button>
                                                )}
                                            </div>
                                        ) : currentOrg.plan === 'free' && onUpgradePlan ? (
                                            <Button
                                                variant="primary"
                                                onClick={handleUpgrade}
                                                disabled={upgradeLoading}
                                            >
                                                {upgradeLoading ? 'Upgrading...' : 'Upgrade to Pro'}
                                                <ChevronRight size={18} />
                                            </Button>
                                        ) : currentOrg.plan === 'pro' && onGetDowngradePreview ? (
                                            <button
                                                onClick={handleOpenDowngradeModal}
                                                className="text-[#4f4f48] hover:text-[#0a0a0a] font-medium text-sm underline"
                                            >
                                                Downgrade to Free
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Downgrade Modal */}
                        {showDowngradeModal && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                                <div className="bg-white rounded-2xl border-2 border-[#0a0a0a] nb-shadow-lg p-6 max-w-md w-full">
                                    <h3 className="text-lg font-bold text-[#0a0a0a] mb-1">Cancel Pro subscription?</h3>
                                    <p className="text-[#4f4f48] text-sm mb-4">
                                        You&apos;ll keep Pro until the end of your current billing period, then your workspace moves to the Free plan. You won&apos;t be charged again.
                                    </p>

                                    {downgradePreview && downgradePreview.rules_to_disable.length > 0 && (
                                        <div className="mb-4 p-4 bg-[#fef3c7] border border-[#fde68a] rounded-lg">
                                            <p className="text-[#92600a] font-medium mb-2">
                                                When your subscription ends, these rules will be disabled:
                                            </p>
                                            <ul className="text-[#92600a] text-sm space-y-1">
                                                {downgradePreview.rules_to_disable.map(rule => (
                                                    <li key={rule.id} className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 bg-[#d97706] rounded-full" />
                                                        {rule.name}
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className="text-[#92600a] text-xs mt-2">
                                                Your oldest active rule will remain enabled. Until the period ends, all rules stay active.
                                            </p>
                                        </div>
                                    )}

                                    <div className="mb-4">
                                        <label className="block text-sm font-semibold text-[#4f4f48] mb-1.5">
                                            Reason for downgrading (optional)
                                        </label>
                                        <textarea
                                            value={downgradeReason}
                                            onChange={(e) => setDowngradeReason(e.target.value)}
                                            placeholder="Help us improve by sharing your feedback..."
                                            className="w-full rounded-xl border border-[#d3cdba] bg-white px-3.5 py-2.5 text-sm font-medium text-[#0a0a0a] transition-all placeholder:text-[#9a948a] focus:border-[#22c55e] focus:outline-none focus:ring-2 focus:ring-[#22c55e]/30 resize-none h-24"
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowDowngradeModal(false);
                                                setDowngradeReason('');
                                                setDowngradePreview(null);
                                            }}
                                            className="flex-1"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={handleConfirmDowngrade}
                                            disabled={downgradeLoading}
                                            className="flex-1"
                                        >
                                            {downgradeLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </Card>
        </div>
    );
};
